import { useEffect } from 'react';

import { useAtom, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { Button, Flex, Spinner, Text } from 'components/uikit';
import {
  refreshPaymentAtom,
  retryPaymentPostProcessingAtom,
} from 'data/payments/atoms';
import { DepositMethod, DepositPostProcessStatus } from 'data/payments/models';
import { isFormLoading } from 'utils/atoms/dataFetchingAtom';
import { useIsMobile } from 'utils/hooks/screenBreakpoints';

import { PaymentQrLoadingMessages } from './PaymentQrLoadingMessages';

const LOADING_POLL_INTERVAL_MS = 3000;

interface PaymentQrPostProcessStatusProps {
  id: string;
  method: DepositMethod | null;
  status: DepositPostProcessStatus;
  size: number;
}

export const PaymentQrPostProcessStatus = ({
  id,
  method,
  status,
  size,
}: PaymentQrPostProcessStatusProps) => {
  const { t } = useTranslation('payments');
  const isMobile = useIsMobile();
  const refreshPayment = useSetAtom(refreshPaymentAtom);
  const [retryFormState, retry] = useAtom(retryPaymentPostProcessingAtom);
  const isLoading = status === 'LOADING' || isFormLoading(retryFormState);

  useEffect(() => {
    if (status !== 'LOADING') return;

    const interval = setInterval(
      () => refreshPayment(id),
      LOADING_POLL_INTERVAL_MS,
    );

    return () => clearInterval(interval);
  }, [status, id, refreshPayment]);

  return (
    <Flex
      w={`${size}px`}
      h={`${size}px`}
      maxW='100%'
      flexDirection='column'
      justifyContent='center'
      alignItems='center'
      gap='12px'
    >
      {isLoading ? (
        <>
          <Spinner size='lg' />
          {method === 'NSPK_QR' && isMobile && <PaymentQrLoadingMessages />}
        </>
      ) : (
        <>
          <Text fontSize='sm' fontWeight='medium' textAlign='center'>
            {t('payment_qr_failed')}
          </Text>
          <Button variant='payment' onClick={() => retry({ depositId: id })}>
            {t('retry')}
          </Button>
        </>
      )}
    </Flex>
  );
};
